import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const IntakeForm = () => {
  const { t } = useTranslation();
  const { control, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      // Personal Information
      forename: '',
      surname: '',
      phone: '',
      email: '',
      vat: '',
      street: '',
      number: '',
      zipCode: '',
      city: '',
      country: '',
      gender: '',
      dob: '',
      // Treatment Information
      treatmentAreas: '',
      pastTechniques: {
        shaving: false,
        waxing: false,
        laser: false,
        ipl: false,
        tweezing: false,
        other: false,
      },
      pastTechniquesOtherDetails: '', // Renamed from otherText for clarity
      hormonalCondition: 'no', // Default to 'no'
      hormonalConditionDetails: '',
      medication: 'no', // Default to 'no'
      medicationDetails: '',
    }
  });

  const watchPastTechniquesOther = watch('pastTechniques.other', false);
  const watchHormonalCondition = watch('hormonalCondition', 'no');
  const watchMedication = watch('medication', 'no');

  const onSubmit = (data) => {
    console.log('Form Data:', data);
    // Handle form submission logic here
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 divide-y divide-gray-200">
      {/* Section: Personal Information */}
      <div className="pt-8">
        <div>
          <h2 className="text-xl font-semibold leading-6 text-gray-900">{t('sections.personal_information')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('sections.personal_information_description', 'Please fill in your personal details.')}</p>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          {/* Name Fields Row */}
          <div className="sm:col-span-3">
            <label htmlFor="forename" className="block text-sm font-medium text-gray-700">
              {t('questions.forename.label')}
            </label>
            <Controller
              name="forename"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="forename"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.forename ? 'border-red-500' : ''}`}
                  placeholder={t('questions.forename.placeholder')}
                />
              )}
            />
            {errors.forename && <p className="mt-1 text-sm text-red-600">{errors.forename.message}</p>}
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="surname" className="block text-sm font-medium text-gray-700">
              {t('questions.surname.label')}
            </label>
            <Controller
              name="surname"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="surname"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.surname ? 'border-red-500' : ''}`}
                  placeholder={t('questions.surname.placeholder')}
                />
              )}
            />
            {errors.surname && <p className="mt-1 text-sm text-red-600">{errors.surname.message}</p>}
          </div>

          {/* Phone Number */}
          <div className="sm:col-span-3">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              {t('questions.phone.label')}
            </label>
            <Controller
              name="phone"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="tel"
                  id="phone"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.phone ? 'border-red-500' : ''}`}
                  placeholder={t('questions.phone.placeholder')}
                />
              )}
            />
            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
          </div>

          {/* Email */} 
          <div className="sm:col-span-3">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              {t('questions.email.label')}
            </label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('validation.required'),
                pattern: {
                  value: /^\S+@\S+\.\S+$/,
                  message: t('validation.email_format', 'Please enter a valid email address.')
                }
              }}
              render={({ field }) => (
                <input
                  {...field}
                  type="email"
                  id="email"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.email ? 'border-red-500' : ''}`}
                  placeholder={t('questions.email.placeholder')}
                />
              )}
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
          </div>

          {/* VAT */} 
          <div className="sm:col-span-3">
            <label htmlFor="vat" className="block text-sm font-medium text-gray-700">
              {t('questions.vat.label')}
            </label>
            <Controller
              name="vat"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="vat"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.vat ? 'border-red-500' : ''}`}
                  placeholder={t('questions.vat.placeholder')}
                />
              )}
            />
            {errors.vat && <p className="mt-1 text-sm text-red-600">{errors.vat.message}</p>}
          </div>

          {/* Gender Select Dropdown */}
          <div className="sm:col-span-3">
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
              {t('questions.gender.label')}
            </label>
            <Controller
              name="gender"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <select
                  {...field}
                  id="gender"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.gender ? 'border-red-500' : ''}`}
                >
                  <option value="" disabled>{t('form.select_option', 'Please select...')}</option>
                  {['male', 'female', 'other', 'prefer_not_to_say'].map((option) => (
                    <option key={option} value={option}>
                      {t(`questions.gender.options.${option}`)}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.gender && <p className="mt-1 text-sm text-red-600">{errors.gender.message}</p>}
          </div>

          {/* Date of Birth */}
          <div className="sm:col-span-3">
            <label htmlFor="dob" className="block text-sm font-medium text-gray-700">
              {t('questions.dob.label')}
            </label>
            <Controller
              name="dob"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="date"
                  id="dob"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.dob ? 'border-red-500' : ''}`}
                />
              )}
            />
            {errors.dob && <p className="mt-1 text-sm text-red-600">{errors.dob.message}</p>}
          </div>

          {/* Street and Number Row */}
          <div className="sm:col-span-4">
            <label htmlFor="street" className="block text-sm font-medium text-gray-700">
              {t('questions.street.label')}
            </label>
            <Controller
              name="street"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="street"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.street ? 'border-red-500' : ''}`}
                  placeholder={t('questions.street.placeholder')}
                />
              )}
            />
            {errors.street && <p className="mt-1 text-sm text-red-600">{errors.street.message}</p>}
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="number" className="block text-sm font-medium text-gray-700">
              {t('questions.number.label')}
            </label>
            <Controller
              name="number"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="number"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.number ? 'border-red-500' : ''}`}
                  placeholder={t('questions.number.placeholder')}
                />
              )}
            />
            {errors.number && <p className="mt-1 text-sm text-red-600">{errors.number.message}</p>}
          </div>

          {/* ZIP Code and City Row */}
          <div className="sm:col-span-2">
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
              {t('questions.zipCode.label')}
            </label>
            <Controller
              name="zipCode"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="zipCode"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.zipCode ? 'border-red-500' : ''}`}
                  placeholder={t('questions.zipCode.placeholder')}
                />
              )}
            />
            {errors.zipCode && <p className="mt-1 text-sm text-red-600">{errors.zipCode.message}</p>}
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              {t('questions.city.label')}
            </label>
            <Controller
              name="city"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="city"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.city ? 'border-red-500' : ''}`}
                  placeholder={t('questions.city.placeholder')}
                />
              )}
            />
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>}
          </div>
          
          {/* Country */}
          <div className="sm:col-span-2">
            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
              {t('questions.country.label')}
            </label>
            <Controller
              name="country"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  id="country"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.country ? 'border-red-500' : ''}`}
                  placeholder={t('questions.country.placeholder')}
                />
              )}
            />
            {errors.country && <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>}
          </div>
        </div>
      </div>

      {/* Section: Treatment Information */}
      <div className="pt-8">
        <div>
          <h2 className="text-xl font-semibold leading-6 text-gray-900">{t('sections.treatment_information')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('sections.treatment_information_description', 'Please provide details about your treatment.')}</p>
        </div>
        <div className="mt-6 space-y-6">
          {/* Question 1: Treatment areas */}
          <div>
            <label htmlFor="treatmentAreas" className="block text-sm font-medium text-gray-700">
              {t('questions.treatment_areas.label')}
            </label>
            <Controller
              name="treatmentAreas"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <textarea
                  {...field}
                  id="treatmentAreas"
                  rows="3"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.treatmentAreas ? 'border-red-500' : ''}`}
                  placeholder={t('questions.treatment_areas.placeholder')}
                />
              )}
            />
            {errors.treatmentAreas && <p className="mt-1 text-sm text-red-600">{errors.treatmentAreas.message}</p>}
          </div>

          {/* Question 2: Past hair removal techniques */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('questions.past_techniques.label')}
            </label>
            <fieldset className="mt-2">
              <legend className="sr-only">Past hair removal techniques</legend>
              <div className="space-y-2 sm:flex sm:items-center sm:space-y-0 sm:space-x-4">
                {['shaving', 'waxing', 'laser', 'ipl', 'tweezing', 'other'].map((technique) => (
                  <div key={technique} className="flex items-center">
                    <Controller
                      name={`pastTechniques.${technique}`}
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id={`pastTechniques-${technique}`}
                          type="checkbox"
                          checked={field.value || false}
                          className="h-4 w-4 text-clinic-accent border-gray-300 rounded focus:ring-clinic-accent"
                        />
                      )}
                    />
                    <label htmlFor={`pastTechniques-${technique}`} className="ml-2 block text-sm text-gray-900">
                      {t(`questions.past_techniques.options.${technique}`)}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
            {watchPastTechniquesOther && (
              <div className="mt-2">
                <Controller
                  name="pastTechniquesOtherDetails"
                  control={control}
                  rules={{ required: watchPastTechniquesOther ? t('validation.required') : false }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className={`block w-full sm:w-1/2 rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.pastTechniquesOtherDetails ? 'border-red-500' : ''}`}
                      placeholder={t('questions.past_techniques.other_placeholder')}
                    />
                  )}
                />
                {errors.pastTechniquesOtherDetails && <p className="mt-1 text-sm text-red-600">{errors.pastTechniquesOtherDetails.message}</p>}
              </div>
            )}
          </div>

          {/* Question 3 & 4: Hormonal Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('questions.hormonal_condition.label')}
            </label>
            <fieldset className="mt-2">
              <legend className="sr-only">Hormonal condition options</legend>
              <div className="space-y-2">
                {['yes_doctor', 'yes', 'no', 'unknown'].map((option) => (
                  <div key={option} className="flex items-center">
                    <Controller
                      name="hormonalCondition"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id={`hormonalCondition-${option}`}
                          type="radio"
                          value={option}
                          checked={field.value === option}
                          className="h-4 w-4 text-clinic-accent border-gray-300 focus:ring-clinic-accent"
                        />
                      )}
                    />
                    <label htmlFor={`hormonalCondition-${option}`} className="ml-2 block text-sm text-gray-900">
                      {t(`questions.hormonal_condition.options.${option}`)}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
            {(watchHormonalCondition === 'yes' || watchHormonalCondition === 'yes_doctor') && (
              <div className="mt-4">
                <label htmlFor="hormonalConditionDetails" className="block text-sm font-medium text-gray-700">
                  {t('questions.hormonal_condition.details_label', 'If yes or unsure: Which hormonal condition and since when?')}
                </label>
                <Controller
                  name="hormonalConditionDetails"
                  control={control}
                  rules={{ required: (watchHormonalCondition === 'yes' || watchHormonalCondition === 'yes_doctor') ? t('validation.required') : false }}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      id="hormonalConditionDetails"
                      rows="3"
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.hormonalConditionDetails ? 'border-red-500' : ''}`}
                      placeholder={t('questions.hormonal_condition.details_placeholder', 'Specify hormonal condition and onset...')}
                    />
                  )}
                />
                {errors.hormonalConditionDetails && <p className="mt-1 text-sm text-red-600">{errors.hormonalConditionDetails.message}</p>}
              </div>
            )}
          </div>

          {/* Question 5: Medication */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('questions.medication.label')}
            </label>
            <fieldset className="mt-2">
              <legend className="sr-only">Medication options</legend>
              <div className="flex items-center space-x-4">
                {['yes', 'no'].map((option) => (
                  <div key={option} className="flex items-center">
                    <Controller
                      name="medication"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id={`medication-${option}`}
                          type="radio"
                          value={option}
                          checked={field.value === option}
                          className="h-4 w-4 text-clinic-accent border-gray-300 focus:ring-clinic-accent"
                        />
                      )}
                    />
                    <label htmlFor={`medication-${option}`} className="ml-2 block text-sm text-gray-900">
                      {t(`questions.medication.options.${option}`)}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
            {watchMedication === 'yes' && (
              <div className="mt-4">
                {/* The user's spec doesn't explicitly ask for 'details_label' for medication, but implies a follow-up if 'yes' */}
                {/* For now, using a generic placeholder if specific label isn't in translations yet */}
                <Controller
                  name="medicationDetails"
                  control={control}
                  rules={{ required: watchMedication === 'yes' ? t('validation.required') : false }}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      id="medicationDetails"
                      rows="3"
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-clinic-accent focus:ring-clinic-accent sm:text-sm ${errors.medicationDetails ? 'border-red-500' : ''}`}
                      placeholder={t('questions.medication.specify_placeholder', 'If yes, please specify medication(s)...')}
                    />
                  )}
                />
                {errors.medicationDetails && <p className="mt-1 text-sm text-red-600">{errors.medicationDetails.message}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section: Important Considerations */}
      <div className="pt-8">
        <div>
          <h2 className="text-xl font-semibold leading-6 text-gray-900">{t('sections.important_considerations')}</h2>
        </div>
        <div className="mt-6 space-y-3 text-sm text-gray-700 prose prose-sm max-w-none">
          <p>{t('important_considerations_text.paragraph1')}</p>
          <p>{t('important_considerations_text.paragraph2')}</p>
          <p>{t('important_considerations_text.paragraph3')}</p>
          <p>{t('important_considerations_text.paragraph4')}</p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-5">
        <div className="flex justify-end">
          <button
            type="submit"
            className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-clinic-accent hover:bg-clinic-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clinic-accent"
          >
            {t('buttons.submit')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default IntakeForm;
